// AGC: FarmProHub Revenues Hook
// AGC: Tracks revenue data from StakingRevenueShare contract and marketplace contracts

import { useState, useEffect, useCallback } from 'react'
import { useAccount, useReadContract, usePublicClient } from 'wagmi'
import { parseAbi, formatEther, formatUnits } from 'viem'
import { useStaking } from './useStaking'

// AGC: StakingRevenueShareV3 ABI
const REVENUE_SHARE_ABI = parseAbi([
  'function getDistributionStats() external view returns (uint256 totalEth, uint256 totalOgb, uint256 totalUsdc)',
  'function getBalances() external view returns (uint256 ethBalance, uint256 ogbBalance, uint256 usdcBalance)',
  'function getPendingRewards(address user, uint256 week) external view returns (uint256 ethAmount, uint256 ogbAmount, uint256 usdcAmount)',
  'function getWeekRevenue(uint256 week) external view returns (uint256 ethAmount, uint256 ogbAmount, uint256 usdcAmount, bool finalized)',
  'function getStakerCount() external view returns (uint256)',
])

// AGC: ERC20 ABI for reading balances
const ERC20_ABI = parseAbi([
  'function balanceOf(address account) external view returns (uint256)',
])

// AGC: Contract addresses from env
const REVENUE_SHARE_ADDRESS = (import.meta.env.VITE_STAKING_REVENUE_SHARE_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const BUFF1155_ADDRESS = (import.meta.env.VITE_BUFF1155_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const CLASSIC_POTION1155_ADDRESS = (import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const OGB_TOKEN_ADDRESS = (import.meta.env.VITE_OGB_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const USDC_ADDRESS = (import.meta.env.VITE_USDC_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`

// AGC: Week calculation constants (matches contract)
const WEEK_EPOCH = 1704067200 // Jan 1, 2024 00:00 UTC
const WEEK_DURATION = 7 * 24 * 60 * 60 // 7 days in seconds

// AGC: Calculate current week number
const getCurrentWeek = (): bigint => {
  const now = Math.floor(Date.now() / 1000)
  const weeksSinceEpoch = Math.floor((now - WEEK_EPOCH) / WEEK_DURATION)
  return BigInt(weeksSinceEpoch)
}

export interface ProjectRevenue {
  id: string
  name: string
  icon: string
  totalRevenue: number
  stakersShare: number
  thisMonth: number
  status: 'live' | 'upcoming'
}

export interface MarketplaceRevenue {
  buff1155: {
    eth: bigint
    ogb: bigint
    usdc: bigint
  }
  classicPotion1155: {
    eth: bigint
    ogb: bigint
    usdc: bigint
  }
  total: {
    eth: bigint
    ogb: bigint
    usdc: bigint
  }
}

export interface RevenueStats {
  totalRevenue: number
  stakersShare: number
  yourShare: number
  claimable: number
  thisMonth: number
  marketplaceRevenue: MarketplaceRevenue
  projects: ProjectRevenue[]
  isLoading: boolean
}

export interface UseRevenuesReturn {
  stats: RevenueStats
  refresh: () => void
}

export const useRevenues = (): UseRevenuesReturn => {
  const { address, isConnected } = useAccount()
  const publicClient = usePublicClient()
  const { stats: stakingStats } = useStaking()
  const [isRefreshing, setIsRefreshing] = useState(false)

  // AGC: Read distribution stats (total distributed)
  const { data: distributionStats, refetch: refetchDistributionStats } = useReadContract({
    address: REVENUE_SHARE_ADDRESS,
    abi: REVENUE_SHARE_ABI,
    functionName: 'getDistributionStats',
    query: {
      enabled: isConnected && REVENUE_SHARE_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Read current balances
  const { data: balances, refetch: refetchBalances } = useReadContract({
    address: REVENUE_SHARE_ADDRESS,
    abi: REVENUE_SHARE_ABI,
    functionName: 'getBalances',
    query: {
      enabled: isConnected && REVENUE_SHARE_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Read current week revenue
  const currentWeek = getCurrentWeek()
  const { data: currentWeekRevenue, refetch: refetchCurrentWeek } = useReadContract({
    address: REVENUE_SHARE_ADDRESS,
    abi: REVENUE_SHARE_ABI,
    functionName: 'getWeekRevenue',
    args: [currentWeek],
    query: {
      enabled: isConnected && REVENUE_SHARE_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Read user's pending rewards for current week
  const { data: pendingRewards, refetch: refetchPendingRewards } = useReadContract({
    address: REVENUE_SHARE_ADDRESS,
    abi: REVENUE_SHARE_ABI,
    functionName: 'getPendingRewards',
    args: address ? [address, currentWeek] : undefined,
    query: {
      enabled: !!address && isConnected && REVENUE_SHARE_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: ETH balances are read via useEffect with publicClient.getBalance (see below)
  
  // AGC: Read Buff1155 OGB balance
  const { data: buff1155OgbBalance, refetch: refetchBuff1155Ogb } = useReadContract({
    address: OGB_TOKEN_ADDRESS,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: BUFF1155_ADDRESS !== '0x0000000000000000000000000000000000000000' ? [BUFF1155_ADDRESS] : undefined,
    query: {
      enabled: BUFF1155_ADDRESS !== '0x0000000000000000000000000000000000000000' && OGB_TOKEN_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Read Buff1155 USDC balance
  const { data: buff1155UsdcBalance, refetch: refetchBuff1155Usdc } = useReadContract({
    address: USDC_ADDRESS,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: BUFF1155_ADDRESS !== '0x0000000000000000000000000000000000000000' ? [BUFF1155_ADDRESS] : undefined,
    query: {
      enabled: BUFF1155_ADDRESS !== '0x0000000000000000000000000000000000000000' && USDC_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: ETH balances are read via useEffect with publicClient.getBalance (see below)

  // AGC: Read ClassicPotion1155 OGB balance
  const { data: potion1155OgbBalance, refetch: refetchPotion1155Ogb } = useReadContract({
    address: OGB_TOKEN_ADDRESS,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: CLASSIC_POTION1155_ADDRESS !== '0x0000000000000000000000000000000000000000' ? [CLASSIC_POTION1155_ADDRESS] : undefined,
    query: {
      enabled: CLASSIC_POTION1155_ADDRESS !== '0x0000000000000000000000000000000000000000' && OGB_TOKEN_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Read ClassicPotion1155 USDC balance
  const { data: potion1155UsdcBalance, refetch: refetchPotion1155Usdc } = useReadContract({
    address: USDC_ADDRESS,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: CLASSIC_POTION1155_ADDRESS !== '0x0000000000000000000000000000000000000000' ? [CLASSIC_POTION1155_ADDRESS] : undefined,
    query: {
      enabled: CLASSIC_POTION1155_ADDRESS !== '0x0000000000000000000000000000000000000000' && USDC_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Read ETH balances using publicClient (balanceOf doesn't work for ETH)
  const [buff1155Eth, setBuff1155Eth] = useState<bigint>(0n)
  const [potion1155Eth, setPotion1155Eth] = useState<bigint>(0n)

  useEffect(() => {
    const fetchEthBalances = async () => {
      if (!publicClient) return
      
      if (BUFF1155_ADDRESS !== '0x0000000000000000000000000000000000000000') {
        try {
          const balance = await publicClient.getBalance({ address: BUFF1155_ADDRESS })
          setBuff1155Eth(balance)
        } catch (error) {
          console.error('Error fetching Buff1155 ETH balance:', error)
        }
      }
      
      if (CLASSIC_POTION1155_ADDRESS !== '0x0000000000000000000000000000000000000000') {
        try {
          const balance = await publicClient.getBalance({ address: CLASSIC_POTION1155_ADDRESS })
          setPotion1155Eth(balance)
        } catch (error) {
          console.error('Error fetching ClassicPotion1155 ETH balance:', error)
        }
      }
    }
    
    fetchEthBalances()
  }, [publicClient, BUFF1155_ADDRESS, CLASSIC_POTION1155_ADDRESS])

  // AGC: Extract data
  const totalEthDistributed = distributionStats ? (distributionStats as any)[0] as bigint : 0n
  const totalOgbDistributed = distributionStats ? (distributionStats as any)[1] as bigint : 0n
  const totalUsdcDistributed = distributionStats ? (distributionStats as any)[2] as bigint : 0n

  const ethBalance = balances ? (balances as any)[0] as bigint : 0n
  const ogbBalance = balances ? (balances as any)[1] as bigint : 0n
  const usdcBalance = balances ? (balances as any)[2] as bigint : 0n

  const currentWeekEth = currentWeekRevenue ? (currentWeekRevenue as any)[0] as bigint : 0n
  const currentWeekOgb = currentWeekRevenue ? (currentWeekRevenue as any)[1] as bigint : 0n
  const currentWeekUsdc = currentWeekRevenue ? (currentWeekRevenue as any)[2] as bigint : 0n

  const pendingEth = pendingRewards ? (pendingRewards as any)[0] as bigint : 0n
  const pendingOgb = pendingRewards ? (pendingRewards as any)[1] as bigint : 0n
  const pendingUsdc = pendingRewards ? (pendingRewards as any)[2] as bigint : 0n

  // AGC: Calculate total revenue (distributed + current balances)
  // AGC: Convert to USD equivalent (ETH price ~$3000, OGB ~$0.01, USDC = $1)
  // AGC: For simplicity, we'll use a rough estimate
  const ethPrice = 3000 // USD per ETH
  const ogbPrice = 0.01 // USD per OGB
  
  const totalRevenueEth = Number(formatEther(totalEthDistributed + ethBalance))
  const totalRevenueOgb = Number(formatEther(totalOgbDistributed + ogbBalance))
  const totalRevenueUsdc = Number(formatUnits(totalUsdcDistributed + usdcBalance, 6)) // USDC has 6 decimals
  
  const totalRevenue = (totalRevenueEth * ethPrice) + (totalRevenueOgb * ogbPrice) + totalRevenueUsdc
  
  // AGC: Stakers share is 50% of total revenue
  const stakersShare = totalRevenue * 0.5
  
  // AGC: This month revenue (current week)
  const thisMonthEth = Number(formatEther(currentWeekEth))
  const thisMonthOgb = Number(formatEther(currentWeekOgb))
  const thisMonthUsdc = Number(formatUnits(currentWeekUsdc, 6))
  const thisMonth = (thisMonthEth * ethPrice) + (thisMonthOgb * ogbPrice) + thisMonthUsdc
  
  // AGC: User's claimable rewards
  const claimableEth = Number(formatEther(pendingEth))
  const claimableOgb = Number(formatEther(pendingOgb))
  const claimableUsdc = Number(formatUnits(pendingUsdc, 6))
  const claimable = (claimableEth * ethPrice) + (claimableOgb * ogbPrice) + claimableUsdc

  // AGC: Calculate marketplace revenue (pending collection)
  const marketplaceRevenue: MarketplaceRevenue = {
    buff1155: {
      eth: buff1155Eth,
      ogb: buff1155OgbBalance || 0n,
      usdc: buff1155UsdcBalance || 0n,
    },
    classicPotion1155: {
      eth: potion1155Eth,
      ogb: potion1155OgbBalance || 0n,
      usdc: potion1155UsdcBalance || 0n,
    },
    total: {
      eth: buff1155Eth + potion1155Eth,
      ogb: (buff1155OgbBalance || 0n) + (potion1155OgbBalance || 0n),
      usdc: (buff1155UsdcBalance || 0n) + (potion1155UsdcBalance || 0n),
    },
  }

  // AGC: Calculate marketplace revenue in USD
  const marketplaceRevenueUsd = 
    Number(formatEther(marketplaceRevenue.total.eth)) * ethPrice +
    Number(formatEther(marketplaceRevenue.total.ogb)) * ogbPrice +
    Number(formatUnits(marketplaceRevenue.total.usdc, 6))

  // AGC: Total revenue includes marketplace (pending) + distributed + current balances
  const totalRevenueWithMarketplace = totalRevenue + marketplaceRevenueUsd

  // AGC: Calculate user's share based on their stake percentage
  const yourShare = stakingStats.yourShare > 0 && stakersShare > 0
    ? (stakingStats.yourShare / 100) * stakersShare
    : 0

  // AGC: Refresh function
  const refresh = useCallback(() => {
    setIsRefreshing(true)
    Promise.all([
      refetchDistributionStats(),
      refetchBalances(),
      refetchCurrentWeek(),
      refetchPendingRewards(),
      refetchBuff1155Ogb(),
      refetchBuff1155Usdc(),
      refetchPotion1155Ogb(),
      refetchPotion1155Usdc(),
    ]).finally(() => setIsRefreshing(false))
  }, [
    refetchDistributionStats,
    refetchBalances,
    refetchCurrentWeek,
    refetchPendingRewards,
    refetchBuff1155Ogb,
    refetchBuff1155Usdc,
    refetchPotion1155Ogb,
    refetchPotion1155Usdc,
  ])

  return {
    stats: {
      totalRevenue: totalRevenueWithMarketplace,
      stakersShare: stakersShare + (marketplaceRevenueUsd * 0.5), // 50% of marketplace revenue
      yourShare,
      claimable,
      thisMonth,
      marketplaceRevenue,
      projects: [
        {
          id: 'origami',
          name: 'Origami Battles',
          icon: '🎴',
          totalRevenue: totalRevenueWithMarketplace,
          stakersShare: stakersShare + (marketplaceRevenueUsd * 0.5),
          thisMonth,
          status: 'live' as const,
        },
      ],
      isLoading: isRefreshing,
    },
    refresh,
  }
}

